// utils/agencyRoutes.ts — route allowlist of an agency tenant's admin (M-AG-002).
// Pure so the middleware and its unit test share ONE definition.
import type { UserRole } from '~/types/auth'

export const AGENCY_ADMIN_SETTINGS = [
  '/settings/profile', '/settings/appearance',
  '/settings/fiscal',            // agency emisor (Part 3 switches it to the clinic-level config)
  '/settings/branding',
  '/settings/users',             // general staff list/invite/edit (agencyInviteRoleOptions below
                                  // restricts invites to collection_manager); /cobranza/equipo
                                  // (Part 2, M-AG-003) is the dedicated cobranza-team view — both coexist
  '/settings/staff-assignments', // commission rates of home collectors (spec §5.2)
]

export function isAgencyAdminAllowed(path: string): boolean {
  return path === '/cobranza' || path.startsWith('/cobranza/')
    || path === '/surgeries' || path.startsWith('/surgeries/')
    || path === '/settings'
    || AGENCY_ADMIN_SETTINGS.some(p => path === p || path.startsWith(p + '/'))
}

/**
 * M-AG-003 (P2-R22): visibility of the two agency-home-only sidebar items —
 * '/cobranza/clinicas' and '/cobranza/equipo'. AT HOME ONLY: gated on
 * `isAgencyTenant` (the CURRENT tenant), never on agency *membership*
 * (useCollectionContext().isAgencyMember). A membership stays true even after the
 * member switches into a client clinic (clinicKind 'clinic' there,
 * mig 1230 _apply_collection_context always drops her to role 'collection_manager'
 * outside the agency), and list_agency_engagements() (mig 1231, l.567-570) requires
 * `is_agency_tenant(auth_clinic_id()) OR is_admin()` — a plain collection_manager
 * inside a client clinic hits access_denied there. Using isAgencyMember would show a
 * nav item that bounces straight back on click (middleware/02.permission.global.ts
 * calls this same function for the route guard, so sidebar and middleware can't drift).
 *
 * '/cobranza/clinicas' is visible to ANY role at home, not just admin:
 * list_agency_engagements() explicitly allows a non-admin collection_manager with an
 * ACTIVE membership row (same migration, same lines) — she only sees the engagements
 * she is a member of. '/cobranza/equipo' additionally requires `isAdmin` (team
 * management is admin-only).
 */
export function isAgencyHomeNavVisible(path: string, s: { isAgencyTenant: boolean; isAdmin: boolean }): boolean {
  if (path !== '/cobranza/clinicas' && path !== '/cobranza/equipo' && path !== '/cobranza/plan') return true
  if (!s.isAgencyTenant) return false
  if (path === '/cobranza/equipo' && !s.isAdmin) return false
  return true
}

/**
 * Part 5: a SUSPENDED agency tenant (authStore.isAgencySuspended) can open only
 * «Plan y pagos» plus personal settings; everything else bounces to /cobranza/plan.
 */
export function isAgencySuspendedRouteAllowed(path: string): boolean {
  return path === '/cobranza/plan'
    || path === '/settings/profile' || path.startsWith('/settings/profile/')
    || path === '/settings/appearance' || path.startsWith('/settings/appearance/')
}

/**
 * Ruling R8 (Part 1 fix round 1): an agency tenant's admin may invite ONLY
 * `collection_manager` members. The DB guard (guard_agency_context, mig 1230) blocks
 * role changes of a member who already has an agency_memberships row, but nothing on
 * the server stops INVITING a brand-new profile with a different role — that has to be
 * enforced client-side. Pure so pages/settings/users/invite.vue and
 * components/settings/UserFormModal.vue share one definition and the gate is
 * unit-testable without mounting either (both use vee-validate + ResponsiveDialog,
 * which the project has no existing mount-test fixture for).
 *
 * Fails closed: if `collection_manager` isn't even present in `defaultOptions`
 * (shouldn't happen — every role picker in the app offers it — but if it were ever
 * removed) an agency tenant gets an EMPTY list rather than silently falling back to
 * every option.
 */
/**
 * P3-R21 (controller ruling, data integrity): an agency member working INSIDE
 * a client clinic (useCollectionContext().isInClientClinic — mig 1230 drops
 * her role to collection_manager there, clinicKind 'clinic') must NEVER reach
 * /settings/fiscal. The route is otherwise allowed for any collection_manager
 * (M-PB-212, her own fiscal data) — but stores/doctor_fiscal.ts#save() upserts
 * `clinic_id: settingsStore.clinic!.id` (the CURRENT clinic). Her existing
 * home row is NOT movable from there (mig 050 doctor_fiscal_update USING
 * clinic_id = auth_clinic_id() → the ON CONFLICT update is blocked and the save
 * fails); the real risk is that, with no row in the client clinic, the upsert
 * INSERTs a NEW personal doctor_fiscal_config row there (doctor_fiscal_insert:
 * doctor_id = auth.uid()) — a stray emisor in another tenant (final fix wave
 * M-2). UX guard only: the middleware fails open if the contexts load fails
 * (accepted); a DB-level guard is a follow-up.
 * Pure so the middleware and its unit test share one definition (same pattern
 * as isAgencyHomeNavVisible above).
 */
export function isFiscalRouteBlockedForAgencyMember(path: string, isInClientClinic: boolean): boolean {
  return path === '/settings/fiscal' && isInClientClinic
}

export function agencyInviteRoleOptions<T extends { value: UserRole }>(
  isAgencyTenant: boolean,
  defaultOptions: readonly T[],
): readonly T[] {
  if (!isAgencyTenant) return defaultOptions
  return defaultOptions.filter(o => o.value === 'collection_manager')
}
